/**
 * Shared input mapping + validation for the counter/hedge PRICE LADDER pair of
 * fields (`counterHedgeLadderStepMs`, `counterHedgeLadderAdverseTicks`).
 *
 * The engine walks the counter/hedge leg through three combined-sum targets —
 * the rule threshold, break-even at 1.00, and the pre-authorised
 * `marketHedgeMaxSum` — advancing one rung every `counterHedgeLadderStepMs`
 * milliseconds instead of resting a single order at the ceiling.
 *
 * `counterHedgeLadderStepMs == null` IS the gate: there is no separate toggle,
 * and the field carries no default anywhere in the chain. An EMPTY input
 * therefore means "no ladder", never `0` — `0` is rejected here so an operator
 * who typed it is told which control actually turns the ladder off. A set value
 * must be an integer at or above `COUNTER_LADDER_MIN_STEP_MS`.
 *
 * `counterHedgeLadderAdverseTicks` is the one field of the two the spec gives a
 * default (2, to be applied engine-side when it is absent), so `0` is a real,
 * accepted value there: it disables the adverse-move branches while leaving the
 * timer ladder running. ⚠️ Do NOT read that default off the engine's existing
 * `adverse_cancel_ticks` (`rule.rs`, `adverse_cancel_ticks_resolved()`): that is
 * the maker adverse-CANCEL threshold, a different mechanism that happens to
 * default to 2 as well. The ladder's own field does not exist on `main` yet.
 *
 * Both parsers map blank to `null`. What `null` MEANS on the wire is the
 * caller's decision, and the two group dialogs deliberately disagree:
 * `CreateGroupDialog` sends an explicit `null` on edit (clear the group
 * fallback) and omits the key on create, while `SubgroupConfigDialog` omits the
 * key from its snapshot so Apply leaves member rules untouched.
 */

/**
 * Hard floor on the rung interval, from the spec
 * (`arbitrage-core/docs/specs/2026-08-18-opener-counter-hedge-price-ladder.md`):
 * below the venues' own cancel + confirm + create round trip the extra rungs
 * are not faster, just extra venue load.
 *
 * ⚠️ NOTHING on the backend knows this number — or these two fields — yet.
 * `counterHedgeLadder*` appears nowhere in `arbitrage-core` on `main` outside
 * that spec; the engine-side `COUNTER_LADDER_MIN_STEP_MS` (which clamps a
 * sub-floor interval UP rather than honouring it) lives only on the unmerged
 * branch `feat/counter-hedge-price-ladder`. So the spec's "a positive value
 * under the floor is rejected 400 by the Node DTOs" describes a contract those
 * DTOs will implement, not today's behaviour, and this FE check is currently
 * the ONLY thing standing between an operator and a sub-floor value.
 */
export const COUNTER_LADDER_MIN_STEP_MS = 250;

/** Section heading both group dialogs put the two fields under. */
export const LADDER_SECTION_LABEL = 'Hedge ladder';
export const LADDER_STEP_MS_LABEL = 'Step (ms)';
export const LADDER_ADVERSE_TICKS_LABEL = 'Adverse (ticks)';

export const LADDER_STEP_MS_ERROR = `Must be an integer ≥ ${COUNTER_LADDER_MIN_STEP_MS} ms — leave blank to turn the ladder off`;
export const LADDER_ADVERSE_TICKS_ERROR = 'Must be an integer ≥ 0';

/**
 * The two things an operator cannot read off the fields themselves, and both
 * cost real money to learn the hard way: the ladder is inert under any hedge
 * mode other than `gtc-to-max-sum`, and the interval is read when a unit is
 * SPAWNED, so flipping it does nothing to the cycles already running. (Per the
 * spec — see the note on the help constants below.)
 */
export const LADDER_STEP_MS_HELP =
  'Counter/hedge ladder: walk the hedge across threshold → 1.00 → max sum, one rung every N ms. Blank = off. Only does anything on gtc-to-max-sum; on/off takes effect at the next unit spawn, not on units already running.';

/**
 * ⚠️ These two strings, unlike the comments in this file, describe what the
 * FEATURE does once the engine ships it — that is their job, and the two facts
 * they carry (gtc-to-max-sum only, next unit spawn) are the ones an operator
 * cannot infer from the fields. They are deliberately NOT hedged with "specced";
 * the deploy order is backend-first, so by the time this text is read in
 * production the behaviour it describes exists.
 */
export const LADDER_ADVERSE_TICKS_HELP =
  'Ticks the maker leg’s own book must move for the ladder to skip straight to max sum (adverse) or raise the appreciation alert (favourable). Blank = the default of 2. 0 disables both branches; the timed ladder still advances.';

/**
 * A parsed ladder field. `error` non-null means the operator typed something
 * invalid — `value` is then `null` and callers must NOT ship it, since `null`
 * on the wire is a meaningful value (off / leave alone) rather than "ignore me".
 */
export interface ParsedLadderField {
  value: number | null;
  error: string | null;
}

/** Raw input string → `counterHedgeLadderStepMs`. Empty = `null` (no ladder). */
export function parseLadderStepMsInput(raw: string): ParsedLadderField {
  const trimmed = raw.trim();
  if (trimmed === '') return { value: null, error: null };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < COUNTER_LADDER_MIN_STEP_MS) {
    return { value: null, error: LADDER_STEP_MS_ERROR };
  }
  return { value: n, error: null };
}

/**
 * Raw input string → `counterHedgeLadderAdverseTicks`. Empty = `null`, which the
 * spec has resolving to 2 engine-side; `0` is accepted and means "disable the
 * branches".
 */
export function parseLadderAdverseTicksInput(raw: string): ParsedLadderField {
  const trimmed = raw.trim();
  if (trimmed === '') return { value: null, error: null };
  const n = Number(trimmed);
  if (!Number.isInteger(n) || n < 0) {
    return { value: null, error: LADDER_ADVERSE_TICKS_ERROR };
  }
  return { value: n, error: null };
}

/** Rule/group/subgroup field → input string (`null`/absent renders as empty). */
export function ladderFieldToInput(value: number | null | undefined): string {
  return value != null ? String(value) : '';
}
